/**
 * @file RepYourOrdersWidget.js
 * @version 1.3.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-03
 * @modified 2026-10-08
 *
 * What is going out to a rep's customers, by delivery date.
 *
 * Two presentations of one data set:
 *   desktop — a strip of date chips across the top, table below
 *   phone   — one day at a time, swipe left/right between days
 *
 * The whole fortnight is fetched ONCE and held in state; changing day moves an
 * index, never fires a request. A rep in a field on three bars must not wait
 * for a round trip per swipe.
 *
 * Server-side the rows are a UNION of customer orders and purchase orders — see
 * services/repDashboardService.getOrders. Today only the PO side has rows, and
 * they reach a rep purely through a customer-NAME match, which is why an
 * unmatched count is surfaced rather than letting a renamed account quietly
 * vanish from its rep's dashboard.
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import useRepDashboard from './useRepDashboard';
import useSwipe from '../hooks/useSwipe';
import { formatQuantity } from '../utils/numberFormatters';
import './RepWidgets.css';

const iso = (d) => d.toISOString().slice(0, 10);

/** Two days back, eleven forward — this week, plus enough history to check. */
const defaultRange = () => {
  const today = new Date();
  const from = new Date(today); from.setDate(from.getDate() - 2);
  const to = new Date(today); to.setDate(to.getDate() + 11);
  return { from: iso(from), to: iso(to) };
};

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parseIso = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

function RepYourOrdersWidget({ variant = 'desktop', settings = {}, onSettingsChange }) {
  const [range] = useState(defaultRange);
  // Same zoom control Today's Deliveries carries, and the same 9-18 range, so
  // the two cards sitting side by side can be set to match. A rep's dashboard
  // is locked server-side, so for them this is per-session rather than saved —
  // exactly as it already is on Today's Deliveries.
  const [zoom, setZoom] = useState(settings.zoom || 12);
  useEffect(() => {
    if (settings.zoom !== undefined && settings.zoom !== zoom) setZoom(settings.zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.zoom]);
  const handleZoomChange = (next) => {
    setZoom(next);
    if (onSettingsChange) onSettingsChange({ ...settings, zoom: next });
  };
  const { data, loading, message } = useRepDashboard('orders', { params: range });

  const days = useMemo(() => data?.days || [], [data]);
  const todayIso = iso(new Date());

  // Land on today if it is in range, else the first day carrying anything.
  const [selected, setSelected] = useState(0);
  const settledRef = useRef(false);
  useEffect(() => {
    if (settledRef.current || days.length === 0) return;
    const todayIdx = days.findIndex(d => d.date === todayIso);
    const firstBusy = days.findIndex(d => d.count > 0);
    setSelected(todayIdx >= 0 ? todayIdx : (firstBusy >= 0 ? firstBusy : 0));
    settledRef.current = true;
  }, [days, todayIso]);

  const isPhone = variant === 'phone';
  const clamp = (i) => Math.max(0, Math.min(days.length - 1, i));
  const goTo = (i) => setSelected(clamp(i));

  const swipeRef = useRef(null);
  // Swipe LEFT means "next day" — the content moves left as the next day arrives.
  const { dx } = useSwipe(swipeRef, {
    onLeft: () => goTo(selected + 1),
    onRight: () => goTo(selected - 1),
    enabled: isPhone && days.length > 1
  });

  const day = days[selected];
  const rows = day?.rows || [];

  const renderRows = () => {
    if (rows.length === 0) {
      return <div className="rep-widget-state">Nothing booked for this day.</div>;
    }
    // On desktop this joins the shared table styling in DashboardHome.css that
    // Today's Deliveries and Standing Orders already use — sticky head, row
    // rules, zoomable font — by adding its class to that rule list rather than
    // by reusing .deliveries-table, which carries per-column sizing for the
    // seven-column deliveries table that this four-column one does not want.
    //
    // The phone keeps .rep-orders-table, which is already styled for it.
    const table = (
      // No .rep-orders-table on desktop: that class carries the rep card's own
      // table styling, and it would fight the .deliveries-table rules this is
      // being wrapped in. The phone keeps it.
      <table className={isPhone ? 'rep-orders-table' : ''}>
        <thead>
          <tr>
            <th>Customer</th>
            <th>Item</th>
            <th className="num">Qty</th>
            {!isPhone && <th>Ref</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.source}-${r.id}-${i}`}>
              <td data-label="Customer">
                {r.customer ? (
                  <>
                    <span className="rep-order-customer">{r.customer}</span>
                    {/* First line of the delivery address: two farms on a rep's
                        patch often share most of a name. */}
                    {r.addressLine1 && (
                      <span className="rep-order-addr">{r.addressLine1}</span>
                    )}
                  </>
                ) : <em>Unmatched</em>}
              </td>
              <td data-label="Item">{r.description || r.itemCode || '—'}</td>
              <td data-label="Qty" className="num">
                {r.quantity === null ? '—' : (formatQuantity(r.quantity) || r.quantity)}
              </td>
              {!isPhone && <td data-label="Ref">{r.reference || '—'}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    );
    if (isPhone) return table;
    return <div className="rep-orders-table-wrap" style={{ fontSize: zoom }}>{table}</div>;
  };

  return (
    <div className="dashboard-card rep-widget rep-orders-card">
      <div className="card-header">
        <h3>
          <span className="material-symbols-rounded">sell</span>
          Your Orders
        </h3>
        <div className="header-controls">
          {day && (
            <span className="rep-widget-sub">
              {day.count} {day.count === 1 ? 'line' : 'lines'}
            </span>
          )}
          {!isPhone && (
            <div className="zoom-controls">
              <button className="zoom-btn" onClick={() => handleZoomChange(Math.max(9, zoom - 1))}>−</button>
              <button className="zoom-btn" onClick={() => handleZoomChange(Math.min(18, zoom + 1))}>+</button>
            </div>
          )}
        </div>
      </div>

      <div className="card-body">
        {loading && <div className="rep-widget-state">Loading…</div>}
        {!loading && message && <div className="rep-widget-state">{message}</div>}

        {!loading && !message && days.length > 0 && (
          <>
            {isPhone ? (
              // ── Phone: one day, swipeable, with buttons that do the same job.
              // The buttons are not optional — a swipe is undiscoverable and
              // unreachable by keyboard.
              <div className="rep-orders-phone">
                <div className="rep-day-pager">
                  <button
                    type="button" className="btn-icon"
                    onClick={() => goTo(selected - 1)}
                    disabled={selected === 0}
                    aria-label="Previous day"
                  >
                    <span className="material-symbols-rounded">chevron_left</span>
                  </button>
                  <div className="rep-day-label">
                    <strong>
                      {day.date === todayIso ? 'Today' : WEEKDAY[parseIso(day.date).getDay()]}
                    </strong>
                    <span>
                      {parseIso(day.date).getDate()} {MONTH[parseIso(day.date).getMonth()]}
                    </span>
                  </div>
                  <button
                    type="button" className="btn-icon"
                    onClick={() => goTo(selected + 1)}
                    disabled={selected === days.length - 1}
                    aria-label="Next day"
                  >
                    <span className="material-symbols-rounded">chevron_right</span>
                  </button>
                </div>

                <div
                  className="rep-swipe-surface"
                  ref={swipeRef}
                  style={{ transform: dx ? `translateX(${dx * 0.4}px)` : undefined }}
                >
                  {renderRows()}
                </div>

                <div className="rep-day-dots" aria-hidden="true">
                  {days.map((d, i) => (
                    <span
                      key={d.date}
                      className={`rep-day-dot${i === selected ? ' active' : ''}${d.count > 0 ? ' has-work' : ''}`}
                    />
                  ))}
                </div>
              </div>
            ) : (
              // ── Desktop: the whole fortnight as chips, table below.
              // Empty days render greyed but PRESENT — a day with nothing on it
              // is information, and hiding it would make the strip jump about.
              <div className="rep-orders-desktop">
                <div className="rep-date-strip" role="tablist">
                  {days.map((d, i) => {
                    const dt = parseIso(d.date);
                    return (
                      <button
                        key={d.date}
                        type="button"
                        role="tab"
                        aria-selected={i === selected}
                        className={`rep-date-chip${i === selected ? ' active' : ''}${d.count === 0 ? ' empty' : ''}${d.date === todayIso ? ' today' : ''}`}
                        onClick={() => setSelected(i)}
                      >
                        {/* No per-day count: the chip already dims itself when
                            a day is empty, and the selected day's line count is
                            in the card header. Three numbers about one day, two
                            of them in the same control, is noise. */}
                        <span className="rep-chip-day">{WEEKDAY[dt.getDay()]}</span>
                        <span className="rep-chip-date">{dt.getDate()}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="rep-orders-body">{renderRows()}</div>
              </div>
            )}

            {/* A PO whose customer_name matches no account belongs to nobody's
                patch and is invisible to every rep. Say so rather than letting
                a renamed customer silently disappear. */}
            {data.unmatchedCount > 0 && (
              <div className="rep-orders-note" title="These have a customer name that does not match an account">
                {data.unmatchedCount} order{data.unmatchedCount === 1 ? '' : 's'} in this period
                could not be matched to a customer account.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default RepYourOrdersWidget;
